import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

type menuItem =
  | {
      id: string;
      label: string;
      icon: React.JSX.Element;
      path: string;
      children?: undefined;
      element?: React.JSX.Element;
    }
  | {
      id: string;
      label: string;
      icon: React.JSX.Element;
      path: string;
      children: {
        id: string;
        label: string;
        path: string;
        element?: React.JSX.Element;
      }[];
      element?: React.JSX.Element;
    };

const Sidebar = ({
  sidebarOpen,
  menuItems,
  activeMenu,
  activeSubMenu,
  setActiveMenu,
  setActiveSubMenu,
  setSidebarOpen,
  width,
}: {
  sidebarOpen: boolean;
  menuItems: menuItem[];
  activeMenu: string;
  setActiveMenu: (id: string) => void;
  activeSubMenu: string;
  setActiveSubMenu: (id: string) => void;
  setSidebarOpen: (sidebarOpen: boolean) => void;
  width: number;
}) => {
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {sidebarOpen && (
        <motion.aside
          initial={{ x: -280 }}
          animate={{ x: 0 }}
          exit={{ x: -280 }}
          transition={{ duration: 0.2 }}
          className="w-64 bg-gradient-to-b from-sky-100 to-sky-50 border-r border-sky-200 pt-4 pb-6 flex flex-col h-[calc(100vh-56px)] fixed left-0 top-14 z-20"
        >
          {/* Sidebar Content */}
          <div className="px-4 pb-4">
            <h2 className="text-xs uppercase tracking-wider text-sky-800 font-semibold">
              Main Menu
            </h2>
          </div>

          <nav className="flex-1 overflow-y-auto">
            <ul className="px-2 space-y-1">
              {menuItems.map((item) => (
                <div key={item.id} className="space-y-2">
                  <li>
                    {item?.children != undefined &&
                    item?.children?.length > 0 ? (
                      <SidebarItemWithChildren item={item} />
                    ) : (
                      <SidebarItem item={item} />
                    )}
                  </li>
                  <AnimatePresence>
                    {item?.children != undefined &&
                      item?.children?.length > 0 &&
                      item?.id == activeMenu && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "max-content", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-10 space-y-2"
                        >
                          {item?.children?.map((subitem) => (
                            <li className="list-disc" key={subitem.id}>
                              <button
                                onClick={() => {
                                  if (!subitem?.element) {
                                    toast.info("Page is under development");
                                    return;
                                  }
                                  setActiveMenu(item.id);
                                  setActiveSubMenu(subitem?.id);
                                  navigate(
                                    `/dashboard${
                                      subitem?.path &&
                                      `/${item?.path}/${subitem?.path}`
                                    }`
                                  );
                                  if (width >= 640) {
                                    setSidebarOpen(true);
                                  } else {
                                    setSidebarOpen(false);
                                  }
                                }}
                                className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                  activeSubMenu === subitem.id
                                    ? "bg-gradient-to-r from-sky-400 to-blue-400 text-white shadow-sm"
                                    : "text-gray-700 hover:bg-sky-50"
                                }`}
                              >
                                {subitem.label}
                                {activeSubMenu === subitem.id && (
                                  <div className="ml-auto w-1 h-6 bg-white rounded-full"></div>
                                )}
                              </button>
                            </li>
                          ))}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>
              ))}
            </ul>
          </nav>
        </motion.aside>
      )}
    </AnimatePresence>
  );

  function SidebarItem({ item }: { item: menuItem }) {
    return (
      <button
        onClick={() => {
          if (!item?.element) {
            toast.info("Page is under development");
            return;
          }
          setActiveMenu(item.id);
          setActiveSubMenu("");
          navigate(`/dashboard${item.path && `/` + item.path}`);
          if (width >= 640) {
            setSidebarOpen(true);
          } else {
            setSidebarOpen(false);
          }
        }}
        className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          activeMenu === item.id
            ? "bg-gradient-to-r from-sky-400 to-blue-400 text-white shadow-md"
            : "text-gray-700 hover:bg-sky-50"
        }`}
      >
        <span
          className={`${
            activeMenu === item.id ? "text-white" : "text-sky-600"
          } mr-3`}
        >
          {item.icon}
        </span>
        {item.label}
        {activeMenu === item.id && (
          <div className="ml-auto w-1 h-6 bg-white rounded-full"></div>
        )}
      </button>
    );
  }

  function SidebarItemWithChildren({ item }: { item: menuItem }) {
    return (
      <button
        onClick={() => {
          if (activeMenu == item.id) {
            setActiveMenu("");
            return;
          }
          setActiveMenu(item.id);
        }}
        className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          activeMenu === item.id
            ? "bg-gradient-to-r from-sky-400 to-blue-400 text-white shadow-md"
            : "text-gray-700 hover:bg-sky-50"
        }`}
      >
        <span
          className={`${activeMenu === item.id ? "text-white" : "text-sky-600"} mr-3`}
        >
          {item.icon}
        </span>
        {item.label}
        <span className="ml-auto flex items-center gap-2 text-white">
          {activeMenu == item?.id ? <ChevronUp size={14} /> : <ChevronDown size={14} className="text-gray-500" />}
          {activeMenu === item.id && (
            <div className="w-1 h-6 bg-white rounded-full"></div>
          )}
        </span>
      </button>
    );
  }
};

export default Sidebar;
